import * as tokens from '@utmost.co/utmost-design-tokens';
import React, { forwardRef } from 'react';
import { css, styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import { Box, BoxProps } from '../Box/Box';

export type ActiveDivProps = BpDesignSystem.PropsWithChildren<BoxProps> & {
  /*! set hideFocus to TRUE if element is already wrapped by button, anchor or other accessibility element and you want to hide ActiveDiv focus styling */
  hideFocus?: boolean;
  /*! If you want disable item selection by tab, set tabIndex to -1 */
  tabIndex?: number;
  /*! Choose a proper role: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques */
  role?: string;
};

/*!
 * This component can handle accessibility issues for div elements.
 * Please remember you should always favour a proper HTML element (button, anchor etc..) over the ActiveDiv element
 */
export const ActiveDiv = forwardRef<HTMLDivElement, ActiveDivProps>(
  (
    { role, tabIndex, onKeyDown, onClick, dataTest = 'active-div', hideFocus = false, ...rest }: ActiveDivProps,
    ref
  ) => {
    return (
      <StyledActiveDiv
        role={role || 'button'}
        tabIndex={tabIndex || 0}
        data-test={dataTest}
        onClick={onClick}
        $hideFocus={hideFocus}
        onKeyDown={
          onKeyDown
            ? (event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event)
            : (event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  // onKeyDown and onClick has different events. This situation can potentially create a bug.
                  // First of all this component shouldn't be use. Try to use Button or Anchor or provide valid onKeyDown action.
                  const mouseEvent = {
                    ...event,
                    screenX: 0,
                    screenY: 0,
                    detail: 0,
                    movementX: 0,
                    movementY: 0,
                    pageX: 0,
                    pageY: 0,
                    button: 0,
                    buttons: 0,
                    clientX: 0,
                    clientY: 0
                  } as unknown as React.MouseEvent<HTMLDivElement>;
                  onClick?.(mouseEvent);
                }
              }
        }
        {...rest}
        ref={ref}
      />
    );
  }
);

const StyledActiveDiv = styled(Box)<{ $hideFocus: boolean }>`
  cursor: pointer;
  ${({ $hideFocus }) => (!$hideFocus ? StyledFocusedStyled : StyledUnfocusedStyled)}
  `;

const StyledFocusedStyled = css`
  &.MuiBox-root {
    transition: all 0.2s ease-in-out;
    outline: none;

    &:active,
    &:focus-within,
    &:focus {
      box-shadow: ${tokens.shadowWeak};
    }
  }
`;

const StyledUnfocusedStyled = css`
  cursor: inherit;
`;
